import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { JournalEntry } from '@/api/models';
import { JournalEntryForm } from './JournalEntryForm';

type JournalEntryModalProps = {
    isOpen: boolean;
    onClose: () => void;
    entry?: JournalEntry | null;
    onSaved?: (entry: JournalEntry) => void;
};

/**
 * Modal presentation of {@link JournalEntryForm}. Thin chrome only — all form
 * behaviour lives in the self-contained form so the route and modal share one
 * implementation.
 */
export const JournalEntryModal: React.FC<JournalEntryModalProps> = ({
    isOpen,
    onClose,
    entry = null,
    onSaved,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container className="max-sm:p-0" size="lg">
                    <Modal.Dialog
                        className="w-full max-w-3xl max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:rounded-none"
                        {...getNodeTestIdProp('journal-entry-modal')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('journalEntry.title')}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {t('journalEntry.subtitle')}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="pb-6">
                            <JournalEntryForm
                                entry={entry}
                                onCancel={onClose}
                                onSaved={onSaved}
                            />
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
